import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Plus, Trash2 } from 'lucide-react';

import API from '../../services/api';
import { numberValue, productLabel } from './selloutUtils';

export const makeSellOutReceiptLine = () => ({
  client_id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  real_product: null,
  real_product_id: null,
  real_format_id: null,
  received_qty: '',
  quantity: '',
  quantityRe: '',
  unit_cost: '',
  comment: '',
});

export const resolveReceiptQuantity = (line) => {
  const received = numberValue(line?.received_qty);
  if (received > 0) return received;
  const quantityRe = numberValue(line?.quantityRe);
  if (quantityRe > 0) return quantityRe;
  return numberValue(line?.quantity);
};

export const resolveReceiptSourceField = (line) => {
  if (numberValue(line?.received_qty) > 0) return 'received_qty';
  if (numberValue(line?.quantityRe) > 0) return 'quantityRe';
  if (numberValue(line?.quantity) > 0) return 'quantity';
  return 'received_qty';
};

function ProductAutocomplete({ line, onChange, disabled }) {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState(line.real_product ? [line.real_product] : []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const query = input.trim();
    if (query.length < 2) {
      setOptions(line.real_product ? [line.real_product] : []);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let mounted = true;
    setLoading(true);

    API.get('product/', {
      params: { search: query, limit: 12, active: true },
      signal: controller.signal,
    })
      .then(({ data }) => {
        if (!mounted) return;
        const rows = Array.isArray(data) ? data : data?.results || [];
        const merged = line.real_product
          ? [line.real_product, ...rows.filter((row) => row?.id !== line.real_product?.id)]
          : rows;
        setOptions(merged);
      })
      .catch((error) => {
        if (!mounted) return;
        if (error?.name === 'CanceledError' || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
          return;
        }
        setOptions(line.real_product ? [line.real_product] : []);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [input, line.real_product]);

  return (
    <Autocomplete
      value={line.real_product}
      options={options}
      loading={loading}
      disabled={disabled}
      onInputChange={(_, value) => setInput(value)}
      onChange={(_, product) => {
        onChange({
          real_product: product || null,
          real_product_id: product?.id || null,
          real_format_id: product?.prformat?.id || null,
        });
      }}
      getOptionLabel={productLabel}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      renderInput={(params) => <TextField {...params} size="small" label="Producto real" />}
    />
  );
}

export default function SellOutReceiptLineTable({
  lines,
  onChange,
  errors,
  disabled,
}) {
  const totalReceived = useMemo(
    () => (lines || []).reduce((sum, line) => sum + resolveReceiptQuantity(line), 0),
    [lines]
  );

  const updateLine = (clientId, patch) => {
    onChange((lines || []).map((line) => (
      line.client_id === clientId ? { ...line, ...patch } : line
    )));
  };

  const addLine = () => onChange([...(lines || []), makeSellOutReceiptLine()]);
  const removeLine = (clientId) => onChange((lines || []).filter((line) => line.client_id !== clientId));

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
        <BoxTitle totalReceived={totalReceived} />
        <Button variant="contained" startIcon={<Plus size={17} />} onClick={addLine} disabled={disabled}>
          Agregar linea
        </Button>
      </Stack>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1040 }}>
          <TableHead>
            <TableRow>
              <TableCell>Producto real</TableCell>
              <TableCell width={130} align="right">Recibida</TableCell>
              <TableCell width={120} align="right">quantity</TableCell>
              <TableCell width={120} align="right">quantityRe</TableCell>
              <TableCell width={130} align="right">Costo</TableCell>
              <TableCell width={210}>Comentario</TableCell>
              <TableCell width={52} align="center" />
            </TableRow>
          </TableHead>
          <TableBody>
            {(lines || []).map((line, index) => {
              const lineErrors = errors?.[line.client_id] || [];
              return (
                <TableRow key={line.client_id} sx={{ verticalAlign: 'top' }}>
                  <TableCell>
                    <Stack spacing={0.75}>
                      <ProductAutocomplete
                        line={line}
                        disabled={disabled}
                        onChange={(patch) => updateLine(line.client_id, patch)}
                      />
                      {lineErrors.length > 0 ? (
                        <Typography variant="caption" color="error">
                          {lineErrors.join(' ')}
                        </Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={line.received_qty || ''}
                      disabled={disabled}
                      onChange={(event) => updateLine(line.client_id, { received_qty: event.target.value })}
                      inputProps={{ min: 0 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={line.quantity || ''}
                      disabled={disabled}
                      onChange={(event) => updateLine(line.client_id, { quantity: event.target.value })}
                      inputProps={{ min: 0 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={line.quantityRe || ''}
                      disabled={disabled}
                      onChange={(event) => updateLine(line.client_id, { quantityRe: event.target.value })}
                      inputProps={{ min: 0 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={line.unit_cost || ''}
                      disabled={disabled}
                      onChange={(event) => updateLine(line.client_id, { unit_cost: event.target.value })}
                      inputProps={{ min: 0, step: '0.0001' }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      multiline
                      minRows={1}
                      value={line.comment || ''}
                      disabled={disabled}
                      onChange={(event) => updateLine(line.client_id, { comment: event.target.value })}
                      placeholder={`Linea ${index + 1}`}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Quitar">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeLine(line.client_id)}
                          disabled={disabled || (lines || []).length <= 1}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
}

function BoxTitle({ totalReceived }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="subtitle1" fontWeight={700}>
        Detalle recibido
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Total recibido: {totalReceived}
      </Typography>
    </Stack>
  );
}
